import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { Subject } from 'rxjs';
import _ from 'lodash';

import { Package, PackageItem } from '../../models/package';

/**
 * Dedicated editor for a single package — opened from
 * AccountPackageListComponent. Multi-field form for the package envelope
 * plus an items sub-table of plain-text inclusions.
 *
 * Items are free text (label + optional qty/price/note). No catalogue
 * lookup, no computed price math; `package.price` is the brochure-site
 * headline. See `Conductor/PACKAGES.md` for why we abandoned the earlier
 * catalogue-linked model.
 *
 * Save shape: the mutated Package object is returned via dialogRef.close
 * to the list component, which writes it back into account.data.packages
 * and triggers the standard accountService.patch flow.
 */
@Component({
  selector: 'app-account-package',
  templateUrl: './account-package.component.html',
  styleUrls: ['./account-package.component.scss'],
})
export class AccountPackageComponent implements OnInit, OnDestroy {
  private unsubscribe$ = new Subject<void>();

  /** The package we're editing — a deep clone so cancel reverts cleanly. */
  pkg!: Package;
  isNew = false;

  /** Reactive form mirroring the package envelope. Items live separately. */
  form!: FormGroup;

  /** Items render as their own list. */
  items: PackageItem[] = [];

  constructor(
    public ref: DynamicDialogRef,
    public config: DynamicDialogConfig,
    private fb: FormBuilder
  ) {}

  ngOnInit(): void {
    this.pkg = this.config.data.pkg;
    this.isNew = !!this.config.data.isNew;

    this.form = this.fb.group({
      name: new FormControl(this.pkg.name ?? '', [Validators.required]),
      slug: new FormControl(this.pkg.slug ?? '', [
        Validators.required,
        Validators.pattern(/^[a-z0-9-]+$/),
      ]),
      tagline: new FormControl(this.pkg.tagline ?? ''),
      description: new FormControl(this.pkg.description ?? ''),
      isActive: new FormControl(this.pkg.isActive !== false),
      isFeatured: new FormControl(!!this.pkg.isFeatured),
      price: new FormControl(this.pkg.price ?? null),
    });

    this.items = this.itemsAsList(this.pkg.items);
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  onAddItem(): void {
    const id = `id-item-${this.shortId()}`;
    const nextIndex = this.items.reduce(
      (max, i) => Math.max(max, i._meta.index ?? 0),
      -1
    ) + 1;
    this.items.push({
      _meta: { id, index: nextIndex },
      label: '',
      quantity: 1,
    });
  }

  onRemoveItem(item: PackageItem): void {
    item._meta.deleted = true;
  }

  onRestoreItem(item: PackageItem): void {
    item._meta.deleted = false;
  }

  onSave(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    Object.assign(this.pkg, this.form.value);
    // Drop empty-label items on save — they're noise, and validation only
    // requires a non-empty label when an item exists at all (per PACKAGES.md §4).
    const kept = this.items.filter(
      (i) => i._meta.deleted || (typeof i.label === 'string' && i.label.trim().length > 0)
    );
    this.pkg.items = this.itemsAsMap(kept);
    this.ref.close({ saved: true, pkg: this.pkg });
  }

  onCancel(): void {
    this.ref.close({ saved: false });
  }

  /** Object-collection → array, sorted by _meta.index, deleted items at end. */
  private itemsAsList(raw: { [id: string]: PackageItem } | PackageItem[] | undefined): PackageItem[] {
    if (!raw) return [];
    const list = Array.isArray(raw) ? raw.filter(Boolean) : Object.values(raw).filter(Boolean);
    return _.sortBy(
      list,
      [(i) => (i._meta?.deleted ? 1 : 0), (i) => i._meta?.index ?? Number.MAX_SAFE_INTEGER]
    );
  }

  /** Array → object-collection keyed by _meta.id. */
  private itemsAsMap(list: PackageItem[]): { [id: string]: PackageItem } {
    const out: { [id: string]: PackageItem } = {};
    list.forEach((item, index) => {
      // Re-number indices so they're contiguous regardless of edit history.
      item._meta.index = index;
      out[item._meta.id] = item;
    });
    return out;
  }

  private shortId(): string {
    return Math.random().toString(36).slice(2, 10);
  }
}
